/**
 * VerifyEmailPage — Pantalla de verificación de email.
 *
 * 2 modos:
 *   1. Modo PENDING (sin `?token=` en URL): el usuario llegó aquí porque
 *      el gate del frontend redirigió por `is_verified=false`. Mostramos
 *      su email + botón de reenvío + indicación de revisar bandeja.
 *      Sin opción de saltar.
 *   2. Modo VERIFYING (`?token=XYZ` en URL): se llegó desde el link del
 *      correo. Llamamos al endpoint `/api/auth/verify` y mostramos
 *      success / error.
 *
 * Tras success, recargamos el estado de auth (`/api/auth/me`) y enviamos
 * al usuario al Workspace.
 */
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, RefreshCw, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const tokenFromUrl = searchParams.get('token');
  const [mode, setMode] = useState(tokenFromUrl ? 'verifying' : 'pending');
  const [statusMessage, setStatusMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [resending, setResending] = useState(false);

  const txt = isSpanish ? {
    pendingTitle: 'Verifica tu correo',
    pendingSub: 'Para activar tu cuenta y entrar al Workspace, haz clic en el enlace que enviamos a tu correo.',
    checkInbox: 'Revisa tu bandeja de entrada (y carpeta de SPAM)',
    resend: 'Reenviar verificación',
    resending: 'Enviando...',
    resentOk: 'Correo de verificación reenviado',
    resentErr: 'No pudimos reenviar el correo. Intenta nuevamente en un momento.',
    verifying: 'Verificando tu correo...',
    successTitle: 'Tu correo ha sido verificado',
    successSub: '¡Bienvenido a DamyxIA! Ya puedes usar todas las funciones.',
    enterWorkspace: 'Iniciar Sesión',
    errorTitle: 'No pudimos verificar tu correo',
    errorExpired: 'El enlace expiró o ya fue usado. Solicita uno nuevo.',
    helpExpired: 'Si el enlace expiró, vuelve a iniciar sesión y pide un nuevo correo.',
    notLoggedIn: 'Inicia sesión para reenviar',
  } : {
    pendingTitle: 'Verify your email',
    pendingSub: 'To activate your account and enter the Workspace, click the link we sent to your email.',
    checkInbox: 'Check your inbox (and SPAM folder)',
    resend: 'Resend verification',
    resending: 'Sending...',
    resentOk: 'Verification email re-sent',
    resentErr: 'We could not resend the email. Try again in a moment.',
    verifying: 'Verifying your email...',
    successTitle: 'Your email has been verified',
    successSub: 'Welcome to DamyxIA! All features are now unlocked.',
    enterWorkspace: 'Sign In',
    errorTitle: 'We could not verify your email',
    errorExpired: 'The link expired or was already used. Request a new one.',
    helpExpired: 'If the link expired, log in again and request a new email.',
    notLoggedIn: 'Log in to resend',
  };

  // Cargar email del usuario autenticado para mostrar a quién le enviamos el código.
  useEffect(() => {
    if (mode !== 'pending') return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        if (res?.data?.email) setUserEmail(res.data.email);
        // Si ya está verificado, no debería estar acá — mandar al home.
        if (res?.data?.is_verified) {
          navigate('/', { replace: true });
        }
      } catch (_) {
        // No autenticado — el botón Reenviar mostrará un mensaje.
      }
    })();
  }, [mode, navigate]);

  const verifyToken = useCallback(async (token) => {
    setMode('verifying');
    try {
      const res = await axios.get(`${API_URL}/api/auth/verify?token=${encodeURIComponent(token)}`);
      const data = res?.data || {};
      // Mensaje contextual segun el `code` estructurado del backend.
      if (data.code === 'ALREADY_VERIFIED') {
        setStatusMessage(isSpanish
          ? 'Tu email ya estaba verificado. Puedes entrar al Workspace.'
          : 'Your email was already verified. You can enter the Workspace.');
      } else {
        setStatusMessage(data.message || '');
      }
      setMode('success');
      // Forzar refresh del estado del frontend para que el gate desaparezca.
      try { await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true }); } catch (_) {}
    } catch (error) {
      const data = error?.response?.data || {};
      const code = data.code;
      let msg = data.message || data.detail || txt.errorExpired;
      // Mensaje afinado para el caso mas frecuente.
      if (code === 'EXPIRED_TOKEN') {
        msg = isSpanish
          ? 'Tu enlace expiró. Pide un correo nuevo desde el botón "Reenviar verificación".'
          : 'Your link expired. Request a new one with "Resend verification".';
      }
      setStatusMessage(typeof msg === 'string' ? msg : txt.errorExpired);
      setMode('error');
    }
  }, [txt.errorExpired, isSpanish]);

  useEffect(() => {
    if (tokenFromUrl) {
      verifyToken(tokenFromUrl);
    }
  }, [tokenFromUrl, verifyToken]);

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, {}, { withCredentials: true });
      toast.success(txt.resentOk);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        toast.error(txt.notLoggedIn);
        navigate('/login');
      } else {
        toast.error(txt.resentErr);
      }
    } finally {
      setResending(false);
    }
  };

  const handleEnterWorkspace = () => {
    // Redirige directo al formulario de login (`/login`) para que el usuario
    // pueda ingresar sus credenciales sin pasar por la pantalla Welcome.
    // Hard-reload para que App.js relea is_verified=true desde /api/auth/me.
    window.location.href = '/login';
  };

  return (
    <div className="min-h-[100dvh] bg-zinc-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center" data-testid="verify-email-card">
          {/* Icon */}
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${
            mode === 'verifying' ? 'bg-indigo-500/20' :
            mode === 'success' ? 'bg-green-500/20' :
            mode === 'error' ? 'bg-red-500/20' :
            'bg-amber-500/20'
          }`}>
            {mode === 'verifying' && <Loader2 className="w-10 h-10 text-indigo-400 animate-spin" />}
            {mode === 'success' && <CheckCircle className="w-10 h-10 text-green-400" />}
            {mode === 'error' && <XCircle className="w-10 h-10 text-red-400" />}
            {mode === 'pending' && <Mail className="w-10 h-10 text-amber-400" />}
          </div>

          {/* Title */}
          <h1 className={`text-2xl font-bold mb-2 ${
            mode === 'success' ? 'text-green-400' :
            mode === 'error' ? 'text-red-400' :
            'text-white'
          }`} data-testid="verify-email-title">
            {mode === 'verifying' && txt.verifying}
            {mode === 'success' && txt.successTitle}
            {mode === 'error' && txt.errorTitle}
            {mode === 'pending' && txt.pendingTitle}
          </h1>

          {/* Body */}
          {mode === 'pending' && (
            <>
              <p className="text-zinc-400 mb-3">{txt.pendingSub}</p>
              {userEmail && (
                <p className="text-sm font-mono text-indigo-300 bg-indigo-950/40 border border-indigo-900/60 rounded-lg py-2 px-3 mb-4 break-all" data-testid="verify-email-address">
                  {userEmail}
                </p>
              )}
              <p className="text-zinc-500 text-sm mb-6">{txt.checkInbox}</p>
              <button
                onClick={handleResend}
                disabled={resending}
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-700 text-white rounded-xl transition-colors w-full justify-center"
                data-testid="resend-verification-btn"
              >
                {resending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {txt.resending}
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    {txt.resend}
                  </>
                )}
              </button>
            </>
          )}

          {mode === 'verifying' && (
            <p className="text-zinc-400 mb-2">{txt.verifying}</p>
          )}

          {mode === 'success' && (
            <>
              <p className="text-zinc-300 mb-6">{txt.successSub}</p>
              <button
                onClick={handleEnterWorkspace}
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors w-full justify-center"
                data-testid="enter-workspace-btn"
              >
                {txt.enterWorkspace}
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}

          {mode === 'error' && (
            <>
              <p className="text-zinc-400 mb-6">{statusMessage}</p>
              <button
                onClick={handleResend}
                disabled={resending}
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-700 text-white rounded-xl transition-colors w-full justify-center"
                data-testid="resend-verification-btn"
              >
                {resending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {txt.resending}
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    {txt.resend}
                  </>
                )}
              </button>
              <p className="text-zinc-500 text-xs mt-4">{txt.helpExpired}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
